import keycloak from '../plugins/keycloak'
import { useSnackbarStore } from '@/stores/snackbar'

// How long a request may take before it counts as failed
const timeout = 30000

/**
 * An answer for a request that never got one (network, CORS, timeout), so that callers see a failed response
 * instead of an exception and their loading state ends.
 *
 * @param {number} status
 * @param {string} error
 * @returns {Response}
 */
function failedResponse (status, error) {
  return new Response(JSON.stringify({ status, error }), {
    status,
    statusText: error,
    headers: { 'Content-Type': 'application/json', 'X-Portal-Client-Error': '1' },
  })
}

/**
 * The login has expired or was refused: the user decides when to log in again, so that what was typed is not
 * lost by a redirect.
 */
function reportExpiredSession () {
  useSnackbarStore().$patch({
    color: 'error',
    text: 'Ihre Anmeldung ist abgelaufen.',
    actionText: 'Neu anmelden',
    action: () => keycloak.login(),
    timeout: -1,
    visible: true,
  })
}

function reportUnreachable () {
  useSnackbarStore().$patch({
    color: 'error',
    text: 'Der Server ist gerade nicht erreichbar. Bitte versuchen Sie es später erneut.',
    visible: true,
  })
}

export default {
  baseUrl: '/api',
  baseHeader: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'x-requested-with': 'XMLHttpRequest',
  },

  /**
   * Request-wrap for global settings, interceptions,..: a request with the token of the logged in user.
   *
   * @param {string} url
   * @param {object} options
   * @param {string} options.method
   * @param {any} options.body
   * @param {object} options.headers Additional headers (can override the default)
   * @param {boolean} options.silent No message if the server is unreachable
   * @returns {Promise<Response>}
   */
  async baseFetch (url = '', { method = 'GET', body, headers = {}, silent = false } = {}) {
    try {
      await keycloak.instance.updateToken()
    } catch (error) {
      console.error('Failed to refresh token:', error)
      reportExpiredSession()
      return failedResponse(401, 'Unauthorized')
    }

    const res = await this.request(`${this.baseUrl}/${url}`, {
      method,
      body,
      headers: { ...headers, authorization: `Bearer ${keycloak.instance.token}` },
      silent,
    })

    // The backend refused a token Keycloak still considers valid
    if (res.status === 401 && !res.headers.get('X-Portal-Client-Error')) {
      reportExpiredSession()
    }

    return res
  },

  /**
   * A request without login (public pages).
   *
   * @param {string} url
   * @param {object} options see baseFetch
   * @returns {Promise<Response>}
   */
  publicFetch (url = '', options = {}) {
    return this.request(`${this.baseUrl}/${url}`, options)
  },

  /**
   * fetch with a timeout that never throws: a request without answer gives a 503 (and a message, unless silent).
   *
   * @param {string} fullUrl
   * @param {object} options see baseFetch
   * @returns {Promise<Response>}
   */
  async request (fullUrl, { method = 'GET', body, headers = {}, silent = false } = {}) {
    const mergedHeaders = { ...this.baseHeader, ...headers }

    // "fetch" will handle that for formdata-bodies
    if (body instanceof FormData) {
      delete mergedHeaders['Content-Type']
    }

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeout)

    try {
      return await fetch(fullUrl, { headers: mergedHeaders, method, body, signal: controller.signal })
    } catch (error) {
      console.error(`Request ${method} ${fullUrl} failed:`, error)
      silent || reportUnreachable()
      return failedResponse(503, 'Service Unavailable')
    } finally {
      clearTimeout(timer)
    }
  },
}
